import React, { useMemo } from "react";
import { FormGroup } from "reactstrap";
import { useJnx } from "../../util/jnx";
import getPathFromId from '../../util/getPathFromId';
import { useSideChannelSubscription } from "../../util/useSideChannel";

/**
 * SelectField - A dropdown field that supports ui:onChange
 *
 * Use this field when you need to execute a JSONata expression when
 * the dropdown value changes (e.g., clearing other fields).
 *
 * Usage in schema:
 * {
 *   "region": {
 *     "type": "string",
 *     "title": "Region",
 *     "enum": ["FL", "PR"],
 *     "enumNames": ["Florida", "Puerto Rico"],
 *     "ui:field": "SelectField",
 *     "ui:onChange": {
 *       "expr": "$set({'fieldPath.to.clear': null})",
 *       "scope": ".."
 *     }
 *   }
 * }
 */
function SelectField(props) {
    const {
        formData,
        formContext: {
            setFormDataValues,
            sideChannel,
            bindings: fcBindings,
        },
        disabled,
        readonly: propReadonly,
        schema: {
            title,
            type,
            enum: enumValues,
            enumNames,
        },
        idSchema: { $id } = {},
        uiSchema: {
            'ui:readonly': uiReadonly,
            'ui:onChange': onChangeExpr,
            'ui:placeholder': placeholder,
            'ui:allowSelectNone': allowSelectNone = true,
        },
        required,
        onChange: propOnChange
    } = props;

    const readonly = propReadonly || uiReadonly;
    const path = useMemo(() => getPathFromId($id), [$id]);

    const [rootFormData, formObject] = useSideChannelSubscription(sideChannel, [0, 1]) || [{}, {}];

    const functionBinds = useMemo(() => ({ set: setFormDataValues }), [setFormDataValues]);
    const onChangeJnx = useJnx(onChangeExpr, { functionBinds });

    const bindings = useMemo(() => ({
        ...fcBindings,
        formObject,
        rootFormData,
        fieldPath: path,
    }), [fcBindings, formObject, rootFormData, path]);

    // Build options from enum/enumNames
    const options = useMemo(() => {
        if (!enumValues) return [];
        return enumValues.map((value, idx) => ({
            value,
            label: enumNames?.[idx] || value
        }));
    }, [enumValues, enumNames]);

    // Process value based on type
    const processValue = (value) => {
        if (value === "") return undefined;
        if (type === "number") return Number(value);
        if (type === "boolean") return value === "true";
        return value;
    };

    const onChange = (event) => {
        const newValue = processValue(event.target.value);

        // Call the standard onChange to update the field value
        propOnChange(newValue);

        // Execute ui:onChange expression if defined
        if (onChangeJnx && path) {
            setTimeout(() => {
                try {
                    onChangeJnx.eval(rootFormData || {}, path, {
                        ...bindings,
                        fieldPath: path,
                        object: newValue,
                        value: newValue
                    });
                } catch (error) {
                    console.error('[SelectField] Error in ui:onChange:', error);
                }
            }, 50);
        }
    };

    return (
        <FormGroup disabled={readonly || disabled}>
            {title ? (
                <label className="control-label" htmlFor={$id}>
                    {title}{required ? <span className="required">*</span> : null}
                </label>
            ) : null}
            <select
                id={$id}
                data-cy={$id}
                className="form-control"
                value={formData ?? ""}
                required={required}
                disabled={readonly || disabled}
                onChange={onChange}
            >
                {allowSelectNone && (
                    <option value="">{placeholder || "---"}</option>
                )}
                {options.map(({ value, label }, idx) => (
                    <option key={idx} value={value}>
                        {label}
                    </option>
                ))}
            </select>
        </FormGroup>
    );
}

export default SelectField;
